import { isEqual, differenceWith } from "lodash";

import type { IRange } from "app/pages/sales-intelligence/types/range";
import { is } from "app/pages/sales-intelligence/utils/type-guards";

import type { FilterValueDelta } from "../types/misc";
import { isFilterRange } from "../types/typeGuards";

export const calculateArrayDelta = <T = unknown>(
    previousValue?: readonly T[] | null,
    newValue?: readonly T[] | null,
): FilterValueDelta<T> => {
    if (!previousValue || previousValue.length === 0) {
        return { deleted: [] };
    }

    if (!newValue || newValue.length === 0) {
        return { deleted: previousValue };
    }

    const deleted = differenceWith(previousValue, newValue, isEqual);

    return { deleted };
};

export const calculatePrimitiveDelta = <T>(previousValue?: T, newValue?: T): FilterValueDelta<T> => {
    if (is.undefined(previousValue) || previousValue === null) {
        return { deleted: [] };
    }

    if (is.undefined(newValue) || newValue === null) {
        return { deleted: [previousValue] };
    }

    if (previousValue !== newValue) {
        return { deleted: [previousValue] };
    }

    return { deleted: [] };
};

export const calculateRangeDelta = <T = string>(
    previousValue?: Partial<IRange<T>>,
    newValue?: Partial<IRange<T>>,
): FilterValueDelta<Partial<IRange<T>>> => {
    if (!previousValue) {
        return { deleted: [] };
    }

    if (!newValue) {
        return { deleted: [previousValue] };
    }

    // Check if the entire range object should be considered deleted
    // This happens when either both values were present and now at least one is missing,
    // or when the values have changed
    const hasChanged =
        (!is.undefined(previousValue.from) && previousValue.from !== newValue.from) ||
        (!is.undefined(previousValue.to) && previousValue.to !== newValue.to);

    if (hasChanged) {
        return { deleted: [previousValue] };
    }

    return { deleted: [] };
};

const detectPropertyType = (value: unknown): "range" | "primitive" | "array" | "object" | "unknown" => {
    if (Array.isArray(value)) {
        return "array";
    }

    if (isFilterRange(value)) {
        return "range";
    }

    if (is.string(value) || is.number(value) || is.boolean(value) || value === null) {
        return "primitive";
    }

    return "unknown";
};

export const calculateObjectValueDelta = <T extends Record<string, unknown>>(
    previousValue: T | undefined,
    newValue: T | undefined,
): FilterValueDelta<Partial<T>> => {
    if (!previousValue) {
        return { deleted: [] };
    }

    if (!newValue) {
        return { deleted: [previousValue] };
    }

    const deletedItems: Array<Partial<T>> = [];

    Object.keys(previousValue).forEach((prop) => {
        const key = prop as keyof T;
        const prevValue = previousValue[key];
        const newValueItem = newValue[key];

        if (is.undefined(prevValue)) {
            return;
        }

        const propertyType = detectPropertyType(prevValue);
        let delta: FilterValueDelta;

        switch (propertyType) {
            case "range":
                delta = calculateRangeDelta(prevValue as Partial<IRange>, newValueItem as Partial<IRange> | undefined);
                break;

            case "primitive":
                delta = calculatePrimitiveDelta(prevValue, newValueItem);
                break;

            case "array":
                delta = calculateArrayDelta(prevValue as readonly unknown[], newValueItem as readonly unknown[]);
                break;

            default:
                delta = { deleted: [] };
        }

        if (delta.deleted.length > 0) {
            deletedItems.push({
                [key]: delta.deleted,
            } as Partial<T>);
        }
    });

    return { deleted: deletedItems };
};
